import React from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import StatTile from './StatTile'
import BreakdownBarChart from './BreakdownBarChart'
import BudgetProgressList from './BudgetProgressList'
import StateMixBar from './StateMixBar'

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount)

const monthLabel = (month: string) => {
  const [year, m] = month.split('-')
  const date = new Date(Number(year), Number(m) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short' })
}

type OversightViewProps = {
  scope: 'NATIONAL' | 'BRANCH'
  awaitingMyApproval: number
  oversight: {
    pending_count: number
    is_single_department: boolean
    branch_breakdown: { branch: string; count: number }[]
    department_expense_breakdown: { department: string; amount: number }[]
    state_mix: { state: string; count: number }[]
    processed_amount: number
    processed_count: number
    monthly_volume: { month: string; count: number; total_amount: number }[]
    has_budget_access: boolean
    budget_scope: 'ALL' | 'OWN'
    cost_category_budgets: { category: string; spent: number; budgeted: number }[]
  }
}

const OversightView: React.FC<OversightViewProps> = ({
  scope,
  awaitingMyApproval,
  oversight,
}) => {
  const isNational = scope === 'NATIONAL'
  // A NATIONAL role overseeing a single department (e.g. Jaidad or
  // Tarbiyyat National) -- comparing "departments" is meaningless when
  // there's only ever one, so that panel is skipped in favor of the branch
  // breakdown, which is the axis that actually matters to them.
  const isSingleDepartment = oversight.is_single_department
  const pendingLabel = isNational
    ? `across ${oversight.branch_breakdown.length} branches`
    : `across ${oversight.department_expense_breakdown.length} departments`

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatTile
            label="Awaiting your approval"
            value={awaitingMyApproval}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatTile
            label={isNational ? 'Pending, system-wide' : 'Pending, branch-wide'}
            value={oversight.pending_count}
            caption={pendingLabel}
            color={oversight.pending_count > 0 ? 'warning.main' : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatTile
            label="Processed this financial year"
            value={formatSek(oversight.processed_amount)}
            caption={`${oversight.processed_count} vouchers`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {isNational && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Pending approvals by branch
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Vouchers currently sitting with an approver, grouped by branch
                </Typography>
                <BreakdownBarChart
                  data={oversight.branch_breakdown.map((b) => ({
                    name: b.branch,
                    count: b.count,
                  }))}
                  color="#0d6efd"
                />
              </CardContent>
            </Card>
          )}

          {!isSingleDepartment && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {isNational
                    ? 'Top departments by expense'
                    : 'Top departments by expense in your branch'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Committed spend this financial year (approved + paid)
                </Typography>
                {oversight.department_expense_breakdown.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing spent yet this financial year.
                  </Typography>
                ) : (
                  <BreakdownBarChart
                    data={oversight.department_expense_breakdown.map((d) => ({
                      name: d.department,
                      count: d.amount,
                    }))}
                    color="#20c997"
                    tooltipFormatter={formatSek}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Voucher state mix
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {oversight.state_mix.reduce((sum, s) => sum + s.count, 0)}{' '}
                vouchers on record
              </Typography>
              <StateMixBar data={oversight.state_mix} />
            </CardContent>
          </Card>

          {oversight.has_budget_access && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Cost categories vs. budget
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {oversight.budget_scope === 'ALL'
                    ? 'Heaviest-spending categories this financial year, against their national budget'
                    : "Heaviest-spending categories this financial year, against your department's budget"}
                </Typography>
                {oversight.cost_category_budgets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing spent yet this financial year.
                  </Typography>
                ) : (
                  <BudgetProgressList items={oversight.cost_category_budgets} />
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Six-month volume
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Vouchers created per month
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={oversight.monthly_volume.map((m) => ({
                ...m,
                label: monthLabel(m.month),
              }))}
              margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: number, name: string) =>
                  name === 'count' ? [value, 'Vouchers'] : [formatSek(value), 'Total']
                }
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#0d6efd"
                fill="#0d6efd"
                fillOpacity={0.15}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  )
}

export default OversightView
